// chat/Chat.tsx — the chat panel UI for Muster (cloned from Oriora Lockbox's src/chat/Chat.tsx).
// Purely ADDITIVE: it drives the SAME capabilities the app already has (add / mark status / delete /
// find / recap) by typing. All styling is namespaced under .mst-* so it can't collide with app CSS.
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit Confirm tap
// (a chat message can't destroy data on its own).

import { useState, useRef, useEffect } from "react";
import { askBrain, findUnitByName, type ChatTurn } from "./brain";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type ResultRow } from "./actions";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import CallOverlay from "../live/CallOverlay";
import { platform } from "../tauri-web";

const STORE_KEY = "oriora.muster.chat.v1"; // device-only; lost on clear/reinstall

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    "Tell me what to do — I'll log a unit, mark its progress, find units, or recap your collection.\n\n" +
    'e.g. "add unit Terminator Squad to Space Marines, 200 points, 5 models" · "mark it as painted" · "recap my collection"',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
// (delete_unit removes a unit; backup_restore + import_file REPLACE all data.)
const destructive = (a: ChatAction) =>
  a.kind === "delete_unit" || a.kind === "delete_faction" || a.kind === "delete_paint" || a.kind === "delete_recipe" ||
        a.kind === "backup_restore" || a.kind === "import_file" ||
        a.kind === "library_remove" || a.kind === "library_merge";
const warnFor = (action: ChatAction): string => {
  if (action.kind === "library_merge") return `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`;
  if (action.kind === "library_remove") return `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`;
  if (action.kind === "import_file") return "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel.";
  if (action.kind === "backup_restore") return `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`;
  if (action.kind === "delete_faction") {
    return `Delete ${action.label ?? "this army"} and its units? This can't be undone — tap Confirm, or Cancel to keep it.`;
  }
  const label = ("label" in action && action.label) ? action.label : "this";
  return `Remove ${label} from your collection? This can't be undone — tap Confirm, or Cancel to keep it.`;
};
const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";
type PendingHold = { action: ChatAction; rest: ChatAction[] };

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A destructive action is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  // A photo picked in the composer, waiting to be filed onto a unit. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  const transcriptOf = (t: ChatTurn[]) => t.map((x) => `${x.role}: ${x.content}`).join("\n");

  async function runQueue(queue: ChatAction[], transcript: string): Promise<{ lines: string[]; rows?: ResultRow[]; changed: boolean; hold: PendingHold | null }> {
    const lines: string[] = [];
    let rows: ResultRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1) } };
      }
      const result = await executeAction(action, { transcript });
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  // A photo turn is filed ON-DEVICE onto a unit (its painted-model photo): no model call, no cost.
  // The user names the unit in the same message; fuzzy name-resolution finds the closest one.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const q = text.trim();
      if (!q) {
        setTurns([...next, { role: "assistant", content: "Which unit is this photo for? Name it, then send." }]);
        return; // keep the photo attached so the next message can name it
      }
      const matches = await findUnitByName(q);
      if (matches.length === 0) {
        setTurns([...next, { role: "assistant", content: `I couldn't find a unit matching "${q}". Add it first, then I'll attach the photo.` }]);
        return; // keep the photo attached
      }
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((u) => `• ${u.name}`).join("\n");
        setTurns([...next, { role: "assistant", content: `More than one unit matches — which one?\n${list}` }]);
        return; // keep the photo attached
      }
      const target = matches[0];
      await store.updateUnit(target.id, { photo_ref: photo! });
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `📷 Added your photo to "${target.name}".` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      // Run every non-destructive action in order — two asks in one message must do BOTH (G17).
      const r = await runQueue(actions, transcriptOf(next));
      if (r.changed) onChanged?.();
      if (r.hold) {
        setPending(r.hold);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warnFor(r.hold.action) + tailNote(r.hold.rest)].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines].filter(Boolean).join("\n\n") || "Done.", rows: r.rows }]);
      }
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    const { action, rest } = pending;
    try {
      const lines: string[] = [];
      let rows: ResultRow[] | undefined;
      const result = await executeAction(action, { transcript: transcriptOf(turns) });
      if (result.message) lines.push(result.message);
      if (result.changed) onChanged?.();
      const r = await runQueue(rest, transcriptOf(turns));
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      if (r.changed) onChanged?.();
      if (r.hold) { setPending(r.hold); lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest)); }
      else setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: lines.filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." + (skipped ? ` (Skipped the ${skipped} step${skipped > 1 ? "s" : ""} that were waiting on this.)` : " Tell me what else to do.") }]);
  }

  return (
    <div className="mst-chat">
      <div className="mst-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`mst-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`mst-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <Rows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="mst-msgrow"><div className="mst-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="mst-composer mst-confirm">
          <button className="btn mst-danger" onClick={confirmPending} disabled={busy}>Confirm</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
        <div className="mst-composer">
          <textarea
            value={input}
            placeholder={photo ? "Name the unit…" : "Tell me what to do…"}
            rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <PhotoButton onPhoto={setPhoto} disabled={busy} />
          <MicButton value={input} onChange={setInput} disabled={busy} />
          {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
              own audio (getUserMedia + its echo canceller) — the stack every desktop voice
              assistant uses. */}
          {platform() === "ios" && <button
            type="button"
            className="btn btn-pill"
            title="Talk to it live"
            aria-label="Start a live call"
            style={{ flexShrink: 0, fontSize: 16 }}
            onClick={() => setOnCall(true)}
          >📞</button>}
          {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position sheet,
              so it renders correctly anywhere in the tree, and this needs no structural analysis
              of each app's JSX — a button whose overlay never got mounted is the half-wired
              failure this roll exists to avoid. */}
          {onCall && <CallOverlay
            onClose={() => setOnCall(false)}
            onTranscript={(callLines, seconds) => {
              const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
              setTurns((prev) => [
                ...prev,
                { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                ...callLines.map((l) => ({
                  role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                  content: l.text.trim(),
                })),
              ]);
            }}
          />}
          <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
        </div>
        </>
      )}
    </div>
  );
}

function Rows({ rows }: { rows: ResultRow[] }) {
  return (
    <div className="mst-rows">
      {rows.map((r) => (
        <div key={r.id} className="mst-rowitem">
          <span className="mst-rowmain">
            {r.primary}
            {r.secondary && <span className="muted"> — {r.secondary}</span>}
          </span>
          {r.trailing && <span className="tnum" style={{ fontWeight: 600 }}>{r.trailing}</span>}
        </div>
      ))}
    </div>
  );
}
